<template>
    <div>
        <app-bar :title="advice.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(advice)"
                    >
                        {{ advice.published ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        :title="t('general_title_confirm_action')"
                        @agree="del(advice)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <div class="form-subtitle">{{ t('advice_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="advice.description"></div>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="items()">
                        <template #title>{{ t('advice_label_id') }}: {{ advice.id }}</template>
                    </card-unit>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useEnums } from '@/core/defaults'
    import { Advice } from '@/core/models/advice'
    import moment from 'moment/moment'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardUnit
        },

        /**
         * Component constructor.
         *
         * @param props
         */
        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const enums = useEnums()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
                currentTab: <number | null>null,
            })

            const capture = () => {
                store.dispatch('advice/get', { id: id, payload: { published: '', relations: 'section,stroke' } }).then(() => {
                    state.overlay = false
                })
            }

            const adviceModel = computed(() => <Advice>store.getters['advice/get'])

            const suspend = (adviceModel: Advice) => {
                state.overlay = true
                store.dispatch('advice/update', {
                    id: adviceModel.id,
                    payload: { published: ! adviceModel.published }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'advices.show', params: { id: <any>adviceModel.value.id } })

            const del = (adviceModel: Advice) => {
                state.overlay = true
                store.dispatch('advice/delete', { id: adviceModel.id }).then(() => {
                    router.push({ name: 'advices' })
                })
            }

            const items = () => {
                let items = <any>[
                    { text: i18n.t('advice_label_section'), value: adviceModel.value.section.name },
                    { text: i18n.t('general_label_creation_date'), value: adviceModel.value.createdAt.format('DD/MM/YYYY') },
                    { text: i18n.t('advice_label_connected'), value: enums.adviceConnectTitle(adviceModel.value.connected) },
                    { text: i18n.t('advice_label_connected_id'), value: adviceModel.value.connectedId || '-' },
                    { text: i18n.t('advice_label_stroke'), value: adviceModel.value.stroke?.name || '-' },
                    { text: i18n.t('advice_label_level'), value: enums.levelTitle(adviceModel.value.level) },
                    { text: i18n.t('advice_label_sort_order'), value: adviceModel.value.sortOrder },
                    { text: i18n.t('advice_label_published'), value: adviceModel.value.published ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                    { text: i18n.t('advice_label_author'), value: adviceModel.value.author || '-' },
                ]
                return items
            }

            onMounted(() => {
                capture()
            })

            return {
                capture,
                items,
                advice: adviceModel.value,
                moment,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
